import { Component } from 'react';
import getComponentName from 'react-utils/getComponentName';
import invariant from 'react-utils/invariant';
import { getDefaultPropName, getOnChangeName, getPropFromDefaultName } from '../../utils/PropNames';
import { attachWrappedComponent, makeDecoratorRefCallback } from '../../utils/decoratorUtils';
import { pick } from '../../utils/pick';
import { jsx as _jsx } from "react/jsx-runtime";
export const DEFAULT_FIELDS = ['value'];
function getDefaultProps(component, fields, functionalDefaultProps) {
  // shallow copy of defaultProps to use as the initial value to prevent mutation
  const defaultProps = Object.assign({}, functionalDefaultProps || ('defaultProps' in component ? component.defaultProps : undefined) || {});
  return fields.reduce((defaults, field) => {
    if (Object.prototype.hasOwnProperty.call(defaults, field)) {
      defaults[getDefaultPropName(field)] = defaults[field];
      delete defaults[field];
    }
    return defaults;
  }, defaultProps);
}
function handleChange(instance, field, evt) {
  if (process.env.NODE_ENV !== 'production') {
    invariant(evt && evt.target != null, `${getComponentName(instance.constructor)} handleChange: expected ` + 'argument in shape of SyntheticEvent ({target: value})');
  }
  instance.setState({
    [field]: evt.target.value
  });
  const onChange = instance.props[getOnChangeName(field)];
  if (typeof onChange === 'function') {
    onChange(evt);
  }
}
const PREV_PROP_PREFIX = 'prev_';
const toPrevPropKey = field => `${PREV_PROP_PREFIX}${field}`;
export function Controllable(InputComponent, fields = DEFAULT_FIELDS,
/**
 * Use functionalDefaultProps if you are wrapping a functional component and need to apply defaultProps to the controlled component.
 *
 * Do not pass functionalDefaultProps when wrapping a class component; assign defaultProps to the class component instead.
 *
 * Example:
 * ```tsx
 * Controllable(MyFunctionalComponent, ['isOpen'], { isOpen: false });
 * ```
 */
functionalDefaultProps) {
  const onChangeKeys = fields.map(getOnChangeName);
  const defaultProps = fields.map(getDefaultPropName);
  class controlledComponent extends Component {
    static getDerivedStateFromProps(props, state) {
      const derivedState = {};
      fields.forEach(field => {
        if (state[toPrevPropKey(field)] !== undefined && props[field] === undefined) {
          derivedState[field] = props[getDefaultPropName(field)];
        }
        derivedState[toPrevPropKey(field)] = props[field];
      });
      return derivedState;
    }
    constructor(props) {
      super(props);
      this.handlers = {};
      this.getProps = () => {
        const applicableProps = {};
        Object.keys(this.props).forEach(key => {
          if (defaultProps.indexOf(key) === -1 && this.props[key] !== undefined) applicableProps[key] = this.props[key];
        });
        const applicableState = {};
        Object.keys(this.state).forEach(key => {
          if (!key.startsWith(PREV_PROP_PREFIX)) {
            applicableState[key] = this.state[key];
          }
        });
        return Object.assign({}, applicableState, applicableProps, this.handlers);
      };
      const defaults = pick(props, defaultProps);
      const initialState = {};
      Object.keys(defaults).forEach(defaultKey => {
        initialState[getPropFromDefaultName(defaultKey)] = defaults[defaultKey];
      });
      fields.forEach(field => {
        initialState[toPrevPropKey(field)] = props[field];
      });
      this.state = initialState;
      const handlers = {};
      onChangeKeys.forEach((onChangeKey, i) => {
        handlers[onChangeKey] = evt => handleChange(this, fields[i], evt);
      });
      this.handlers = handlers;
      this._refCallback = makeDecoratorRefCallback(InputComponent, this);
    }
    render() {
      // @ts-ignore InputComponent accepts ref
      return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, this.getProps(), {
        ref: this._refCallback
      }));
    }
  }
  controlledComponent.displayName = `Controllable(${getComponentName(InputComponent)})`;
  controlledComponent.defaultProps = getDefaultProps(InputComponent, fields, functionalDefaultProps);
  attachWrappedComponent(controlledComponent, InputComponent);

  // @ts-ignore $$typeof error - circle back to later
  return controlledComponent;
}